import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';

import { Column, Row } from 'components/shared/Layout';
import { ADD_ADDITIONAL_DETAILS, UPDATE_ADDITIONAL_DETAILS } from 'store/mutations';
import { bookingStatus as bookingStatusType } from 'types/booking';

import { StyledInput, StyledButton, StyledForm, StyledErrorMessage, StyledConfirmButton } from './Minors.styles';

import { setMinors } from 'utils/minors';

const Minors = ({ minorsAmount, minorOutbound, minorInbound, numberOfPassengers, bookingStatus, bookingReference }) => {
  const [isEdit, setEdit] = useState(false);
  const { register, handleSubmit, formState, reset } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      minors: Number(minorsAmount) || '0'
    }
  });

  const { errors } = formState;

  useEffect(() => {
    reset({
      minors: Number(minorsAmount) || '0'
    });
  }, [minorsAmount, reset]);

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading, error: addAdditionalDetailsError }] = useMutation(
    ADD_ADDITIONAL_DETAILS,
    {
      refetchQueries: ['booking']
    }
  );
  const [updateAdditionalDetails, { loading: updateAdditionalDetailsLoading, error: updateAdditionalDetailsError }] = useMutation(
    UPDATE_ADDITIONAL_DETAILS,
    {
      refetchQueries: ['booking']
    }
  );

  const handleEdit = () => setEdit(true);
  const handleSave = async ({ minors }) => {
    await setMinors({
      addAdditionalDetails,
      bookingReference,
      minors,
      minorsAmount,
      minorOutbound,
      minorInbound,
      updateAdditionalDetails
    });
    setEdit(false);
  };

  const isEditable = [bookingStatusType.CONFIRMED, bookingStatusType.ON_HOLD, bookingStatusType.TEMPORARILY_ON_HOLD].includes(
    bookingStatus
  );
  const loading = addAdditionalDetailsLoading || updateAdditionalDetailsLoading;
  const error = addAdditionalDetailsError || updateAdditionalDetailsError;

  return (
    <>
      <Row>
        <StyledForm data-cy="minors-form" data-testid="minors-form" onSubmit={handleSubmit(handleSave)}>
          <FormattedMessage id="minors.numberOfMinors" />
          {isEdit || Number(minorsAmount) ? (
            <StyledInput
              id="minors"
              type="number"
              data-cy="minors-input"
              data-testid="minors-input"
              min={0}
              max={numberOfPassengers}
              disabled={!isEdit}
              {...register('minors', {
                required: 'minors.errorTotalNumber',
                min: {
                  value: 0,
                  message: 'minors.errorTotalNumber'
                },
                max: {
                  value: numberOfPassengers,
                  message: 'minors.errorTotalNumber'
                }
              })}
            />
          ) : (
            <>
              {' '}
              <FormattedMessage id="minors.notSpecified" />
            </>
          )}
          {isEdit ? (
            <>
              <StyledConfirmButton
                disabled={!isEditable || errors?.minors || loading}
                loading={loading}
                type="submit"
                data-testid="minors-save"
                data-cy="minors-save"
              >
                <FormattedMessage id="buttons.save" />
              </StyledConfirmButton>
            </>
          ) : (
            <StyledButton
              disabled={!isEditable || loading}
              loading={loading}
              type="button"
              data-testid="minors-edit"
              data-cy="minors-edit"
              onClick={handleEdit}
            >
              <FormattedMessage id="minors.edit" />
            </StyledButton>
          )}
        </StyledForm>
      </Row>
      {error || errors?.minors ? (
        <Row>
          <Column data-cy="minors-errors">
            <StyledErrorMessage>
              {error?.message || <FormattedMessage id={errors?.minors?.message} values={{ totalPax: numberOfPassengers }} />}
            </StyledErrorMessage>
          </Column>
        </Row>
      ) : null}
    </>
  );
};

Minors.propTypes = {
  minorsAmount: PropTypes.string,
  minorOutbound: PropTypes.string,
  minorInbound: PropTypes.string,
  numberOfPassengers: PropTypes.number,
  bookingStatus: PropTypes.string,
  bookingReference: PropTypes.string
};

export default Minors;
